/**
 * T2-6504 — PII-safe Mixpanel helpers for DO signup contact-info errors.
 */
export type OfficeContactInfoErrorType = 'validation' | 'duplicate' | 'api';

export type OfficeContactInfoErrorPayload = {
  signup_flow: 'office';
  signup_step_number: 3;
  signup_step_name: 'office_contact_info';
  error_type: OfficeContactInfoErrorType;
  failed_fields: string[];
  error_codes: string[];
  filled_field_count: number;
  has_practice_name: boolean;
  has_owner_name: boolean;
  has_phone: boolean;
  has_address_typed: boolean;
  has_address_autocomplete_selection: boolean;
  has_marketing_source: boolean;
  has_dso_affiliation: boolean;
  country: 'CA' | 'US';
};

type BuildArgs = {
  errors: Record<string, string | undefined>;
  practiceName?: string;
  ownersName?: string;
  phone?: string;
  addressTyped?: string;
  hasAutocomplete: boolean;
  marketingSource?: string | null;
  dsoAffiliation?: string | null;
  country: 0 | 1 | number;
  phoneDuplicate?: boolean;
  phoneApiFailed?: boolean;
};

const FIELD_CODE_MAP: Record<string, string> = {
  practiceName: 'practice_name_required',
  ownersName: 'owner_name_required',
  phone: 'phone_invalid',
  address: 'address_invalid',
  country: 'country_required',
  marketingSource: 'marketing_source_required',
  otherSource: 'other_source_invalid',
  dsoAffiliation: 'dso_affiliation_invalid',
};

export function buildOfficeContactInfoErrorPayload(args: BuildArgs): OfficeContactInfoErrorPayload {
  const failed_fields = Object.keys(args.errors).filter((key) => !!args.errors[key]);
  const error_codes = failed_fields.map((field) => {
    if (field === 'phone' && args.phoneDuplicate) return 'phone_duplicate';
    if (field === 'address' && args.addressTyped && !args.hasAutocomplete) return 'address_not_selected';
    if (field === 'address' && /California|CA\b/.test(String(args.errors.address || ''))) {
      return 'address_unsupported_california';
    }
    if (field === 'address' && /building/i.test(String(args.errors.address || ''))) {
      return 'address_missing_building_number';
    }
    if (field === 'marketingSource') return 'marketing_source_required';
    return FIELD_CODE_MAP[field] || `${field}_invalid`;
  });

  let error_type: OfficeContactInfoErrorType = 'validation';
  if (args.phoneDuplicate) error_type = 'duplicate';
  else if (args.phoneApiFailed) error_type = 'api';

  const filledFlags = [
    !!args.practiceName?.trim(),
    !!args.ownersName?.trim(),
    !!args.phone?.trim(),
    !!args.addressTyped?.trim() || args.hasAutocomplete,
    !!args.marketingSource,
    !!args.dsoAffiliation?.trim(),
  ];

  return {
    signup_flow: 'office',
    signup_step_number: 3,
    signup_step_name: 'office_contact_info',
    error_type,
    failed_fields,
    error_codes,
    filled_field_count: filledFlags.filter(Boolean).length,
    has_practice_name: !!args.practiceName?.trim(),
    has_owner_name: !!args.ownersName?.trim(),
    has_phone: !!args.phone?.trim(),
    has_address_typed: !!args.addressTyped?.trim(),
    has_address_autocomplete_selection: args.hasAutocomplete,
    has_marketing_source: !!args.marketingSource,
    has_dso_affiliation: !!args.dsoAffiliation?.trim(),
    country: args.country === 1 ? 'US' : 'CA',
  };
}
